import {
	getApiUrl,
	getUsersIdAvatarPath,
	type MarketResponseDto,
	OrderTimelineKeyEnum,
	type OrderTimelineResponseDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useDateFormat } from "Infrastructure/Utilities/dateFormat";
import { TimelineActionEnum } from "Order/Enum/timelineActionEnum";
import { useFormatTimelineValues } from "Order/Hooks/useFormattedTimelineValue";
import type { ActivePartnerInfo } from "Order/Utilities/deriveActivePartner";
import ProfileImg from "Shared/Assets/Images/Profiles/user-1.jpg";
import {
	TimelineConnector,
	TimelineContent,
	TimelineDot,
	type TimelineDotProps,
	TimelineItem,
	TimelineOppositeContent,
	TimelineSeparator,
} from "@mui/lab";
import { Avatar, Typography } from "@mui/material";
import type { FC } from "react";

const separatorColor: Record<string, TimelineDotProps["color"]> = {
	[TimelineActionEnum.INSERT]: "success",
	[TimelineActionEnum.UPDATE]: "info",
	[TimelineActionEnum.DELETE]: "error",
};

export type TimelineRecordProps = {
	item: OrderTimelineResponseDto;
	isLastItem?: boolean;
	market: MarketResponseDto;
	/** Partner info (ID + assignment timestamp) that was active at the time of this entry */
	activePartnerInfo?: ActivePartnerInfo;
};

const timelineResources = Resources.base.order.detail.timeline;

export const TimelineRecord: FC<TimelineRecordProps> = ({
	item,
	isLastItem,
	market,
	activePartnerInfo,
}) => {
	const { t } = useResource();
	const { formatDateTime } = useDateFormat();
	const formatValue = useFormatTimelineValues(market);

	// Resolve the active partner name from the partner ID
	const activePartnerName = activePartnerInfo
		? formatValue(OrderTimelineKeyEnum.partnerId, activePartnerInfo.partnerId)
		: null;

	const isTimelineAction = Object.values(TimelineActionEnum).includes(
		item.description as TimelineActionEnum,
	);
	const actionTrans = isTimelineAction
		? t(timelineResources.actions[item.description as TimelineActionEnum])
		: t(timelineResources.actions.UPDATE);

	return (
		<TimelineItem>
			<TimelineOppositeContent>
				<Typography>{formatDateTime(item.dateCreated)}</Typography>
			</TimelineOppositeContent>
			<TimelineSeparator>
				<TimelineDot
					color={
						isTimelineAction && item.description
							? separatorColor[item.description]
							: "primary"
					}
					sx={{
						width: 35,
						height: 35,
						padding: 0,
						margin: 0,

						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<Avatar
						src={
							item.userId
								? `${getApiUrl()}${getUsersIdAvatarPath(item.userId)}`
								: ProfileImg
						}
						alt="Profile image"
						sx={{
							width: 30,
							height: 30,
						}}
					/>
				</TimelineDot>
				{!isLastItem && <TimelineConnector />}
			</TimelineSeparator>
			<TimelineContent>
				<Typography component="span">
					{item.userId
						? `${item.user?.name ?? ""} ${item.user?.surname ?? ""}`
						: t(timelineResources.system)}{" "}
				</Typography>
				<Typography component="span">{actionTrans} </Typography>
				<Typography component="span" fontWeight={700} variant="body1">
					{item.key && t(timelineResources.keys[item.key])}
				</Typography>{" "}
				{item.valueFrom && (
					<>
						<Typography component="span">
							{t(timelineResources.from)}{" "}
						</Typography>
						<Typography
							component="span"
							textTransform="uppercase"
							color="secondary"
						>
							{item.key && formatValue(item.key, item.valueFrom)}
						</Typography>{" "}
					</>
				)}
				<>
					{(item.description === TimelineActionEnum.UPDATE ||
						item.key === OrderTimelineKeyEnum.price) && (
						<Typography component="span">to </Typography>
					)}
					<Typography
						component="span"
						textTransform="uppercase"
						color="secondary"
					>
						{item.key && formatValue(item.key, item.valueTo)}
					</Typography>
				</>
				{activePartnerName && (
					<Typography
						component="span"
						variant="body2"
						color="text.secondary"
						sx={{ ml: 0.5 }}
					>
						{" \u2014 "}
						<Typography
							component="span"
							variant="body2"
							fontWeight={600}
							color="text.secondary"
						>
							{activePartnerName}
						</Typography>
					</Typography>
				)}
			</TimelineContent>
		</TimelineItem>
	);
};
